/**
 * CorporateFinancingReadiness — /corporate/financing-readiness
 * Corporate Financing & Transition Readiness assessment
 */
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { RefreshCw, Building2, ChevronRight, CheckCircle2 } from "lucide-react";
import client from "@/api/client";

import { PageHeader, SectionCard, MetricCard, CTAButton } from "@/components/ds";
export default function CorporateFinancingReadiness() {
  const navigate = useNavigate();
  const [portfolio, setPortfolio] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    client.get("/corporate/periodic/portfolio?mode=financing")
      .then(r => setPortfolio(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex items-center justify-center min-h-[40vh]"><RefreshCw size={18} className="animate-spin text-teal-500" /></div>;

  const pd = portfolio?.portfolio_decision;
  const outcome = pd?.outcome_key || "data_improvement_required";

  const OUTCOME_CONFIG = {
    finance_ready:                   { label: "Finance Ready",               color: "emerald", bg: "bg-emerald-50 border-emerald-200 text-emerald-700" },
    conditionally_financeable:       { label: "Conditionally Financeable",   color: "teal",    bg: "bg-teal-50 border-teal-200 text-teal-700" },
    requires_technical_strengthening:{ label: "Requires Technical Strengthening", color: "amber", bg: "bg-amber-50 border-amber-200 text-amber-700" },
    not_financeable:                 { label: "Not Financeable",             color: "red",     bg: "bg-red-50 border-red-200 text-red-700" },
    on_track_with_risks:             { label: "On Track with Risks",         color: "teal",    bg: "bg-teal-50 border-teal-200 text-teal-700" },
    needs_intervention:              { label: "Needs Intervention",          color: "amber",   bg: "bg-amber-50 border-amber-200 text-amber-700" },
    at_risk:                         { label: "At Risk",                     color: "red",     bg: "bg-red-50 border-red-200 text-red-700" },
  };

  const cfg = OUTCOME_CONFIG[outcome] || OUTCOME_CONFIG.at_risk;
  const cred = portfolio?.credibility || {};
  const traj = portfolio?.trajectory || {};

  return (
    <div className="p-6 lg:p-8 space-y-6">
      <PageHeader
        label="CORPORATE WORKSPACE"
        title="Financing & Transition Readiness"
        subtitle="Based on Portfolio Intelligence V3 — Financing Mode"
        backUrl="/corporate"
        backLabel="Corporate Workspace"
      />

      <div className={`rounded-xl border p-5 ${cfg.bg}`}>
        <p className="text-xs font-semibold uppercase tracking-wider mb-1 opacity-70">Financing Readiness</p>
        <p className="text-xl font-bold">{cfg.label}</p>
        <p className="text-xs mt-1 opacity-70">{pd?.description}</p>
        {pd?.portfolio_score != null && <p className="text-xs font-semibold mt-2 opacity-70">Portfolio Score: {pd.portfolio_score}/100</p>}
      </div>

      <div className="grid grid-cols-2 gap-4">
        <MetricCard label="Trajectory" value={traj.label || "—"} sub={`Consistency: ${traj.consistency_index || "—"}/100`} />
        <MetricCard label="Data Credibility" value={cred.label || "—"} sub={`Avg confidence: ${cred.avg_confidence?.toFixed(0) || "—"}/100`} />
      </div>

        {pd?.conditions?.length > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-5">
            <p className="text-xs font-bold text-amber-700 mb-2">Conditions to Address</p>
            {pd.conditions.map((c, i) => <p key={i} className="text-xs text-amber-700 mb-1">→ {c}</p>)}
          </div>
        )}

        <SectionCard>

          <div className="px-5 py-4 border-b border-slate-100">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Improvement Actions</p>
          </div>
          {[
            { label: "Improve monthly data quality",           path: "/corporate/monthly/inputs"   },
            { label: "Lock verified periods",                  path: "/corporate/monthly/inputs"   },
            { label: "Generate Institutional Report",          path: "/corporate/reports"          },
            { label: "Upload evidence to Evidence Vault",      path: "/corporate/evidence-vault"   },
            { label: "Identify carbon monetisation opportunities", path: "/corporate/monetisation" },
          ].map(item => (
            <button key={item.label} onClick={() => navigate(item.path)}
              className="w-full flex items-center justify-between px-5 py-3 border-b border-slate-50 hover:bg-slate-50">
              <p className="text-xs text-slate-600">{item.label}</p>
              <ChevronRight size={11} className="text-slate-300" />
            </button>
          ))}
        

        </SectionCard>
    </div>
  );
}
